import React, { useState, useEffect } from 'react';
import { AdminLogin } from './components/AdminLogin';
import { AdminDashboard } from './components/AdminDashboard';
import { getCurrentUser, supabase } from './lib/supabase';
import { checkAdminStatus } from './lib/adminAuth';
import { createServerSession, hasServerSession } from './lib/serverSession';

interface AdminRouterProps {
  onBackToMain: () => void;
}

export const AdminRouter: React.FC<AdminRouterProps> = ({ onBackToMain }) => {
  const [adminUser, setAdminUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in and is an admin
    const checkAuth = async () => {
      try {
        console.log('🔍 AdminRouter: Checking existing session...');
        const currentUser = await getCurrentUser();

        if (currentUser?.email) {
          console.log('🔍 AdminRouter: Found user session, checking admin status:', currentUser.email);

          // SECURITY: Check if PIN verification was completed in this session
          const pinVerified = sessionStorage.getItem('admin_pin_verified') === 'true';
          if (!pinVerified) {
            console.log('🔒 AdminRouter: PIN not verified in this session, requiring fresh login');
            // Sign out the Supabase auth session to prevent bypass
            await supabase.auth.signOut();
            setAdminUser(null);
            setLoading(false);
            return;
          }

          const adminUser = await checkAdminStatus(currentUser.email);

          if (adminUser) {
            console.log('✅ AdminRouter: Admin session restored:', adminUser.email, adminUser.role);

            // Ensure server-side session exists for API calls (withSessionAuth middleware)
            if (!hasServerSession()) {
              console.log('🔐 AdminRouter: No server session found, creating one...');
              try {
                // Get database user ID (integer) for session creation
                const dbUserId = currentUser.database_user?.id;
                if (dbUserId) {
                  const sessionResult = await createServerSession(dbUserId, currentUser.email, 'email');
                  if (sessionResult.success) {
                    console.log('✅ AdminRouter: Server session created:', sessionResult.sessionId?.substring(0, 8) + '...');
                  } else {
                    console.warn('⚠️ AdminRouter: Failed to create server session:', sessionResult.error);
                  }
                } else {
                  // Fallback: look up DB user ID from users table
                  const { data: dbUser } = await supabase
                    .from('users')
                    .select('id')
                    .eq('email', currentUser.email)
                    .single();
                  if (dbUser?.id) {
                    const sessionResult = await createServerSession(dbUser.id, currentUser.email, 'email');
                    if (sessionResult.success) {
                      console.log('✅ AdminRouter: Server session created (fallback):', sessionResult.sessionId?.substring(0, 8) + '...');
                    }
                  }
                }
              } catch (sessionError) {
                console.warn('⚠️ AdminRouter: Server session creation failed (non-fatal):', sessionError);
              }
            }

            setAdminUser({ ...currentUser, adminUser });
          } else {
            console.log('❌ AdminRouter: User is not an admin, clearing session');
            setAdminUser(null);
          }
        } else {
          console.log('🔍 AdminRouter: No user session found');
        }

        setLoading(false);
      } catch (error) {
        console.error('AdminRouter: Auth check failed:', error);
        setAdminUser(null);
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Handle admin login success
  const handleAdminLogin = (user: any) => {
    setAdminUser(user);
  };

  // Handle admin logout
  const handleAdminLogout = () => {
    sessionStorage.removeItem('admin_pin_verified');
    setAdminUser(null);
    onBackToMain();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading admin portal...</p>
        </div>
      </div>
    );
  }

  // Render admin dashboard if logged in
  if (adminUser) {
    return <AdminDashboard user={adminUser} onLogout={handleAdminLogout} />;
  }

  // Render admin login
  return (
    <>
      <AdminLogin onLoginSuccess={handleAdminLogin} />
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={onBackToMain}
          className="px-4 py-2 bg-gray-800/80 hover:bg-gray-700/80 text-gray-300 hover:text-white rounded-lg transition-colors text-sm backdrop-blur-sm border border-gray-600"
        >
          ← Back to Main Site
        </button>
      </div>
    </>
  );
};
